import { truncateUtf16 } from '@/lib/utils/well-formed-unicode';

/** Collapses whitespace and clips to a positive UTF-16 code-unit limit without splitting surrogate pairs. */
export function clipSingleLineText(text: string | null | undefined, maxLength: number): string {
  const collapsed = (text ?? '').replace(/\s+/g, ' ').trim();
  return collapsed.length > maxLength ? `${truncateUtf16(collapsed, maxLength - 1)}…` : collapsed;
}

/**
 * Converts a string to sentence case (first letter capitalized, rest lowercase)
 * Handles special cases like acronyms and specific words that should remain uppercase
 */
export function toSentenceCase(text: string): string {
  if (!text) {
    return '';
  }

  // Words that should remain uppercase
  const upperCaseWords = [
    'AI',
    'API',
    'URL',
    'ID',
    'UI',
    'UX',
    'PR',
    'CI',
    'CD',
    'SDK',
    'CLI',
    'FAQ',
    'QA',
  ];

  // Check if text starts with a number (with optional periods)
  // Matches: "2.1. Title", "2.1 Title", "2. Title", "2 Title"
  const numberedListMatch = text.match(/^(\d+(?:\.\d+)*\.?)\s+(\S.*)$/);
  if (numberedListMatch && numberedListMatch[2]) {
    const [, number, restOfText] = numberedListMatch;
    return `${number} ${toSentenceCase(restOfText)}`;
  }

  // Split by any whitespace while preserving the whitespace
  const parts = text.split(/(\s+)/);

  let wordIndex = 0;

  return parts
    .map((part) => {
      // If it's whitespace, return as-is
      if (/^\s+$/.test(part)) {
        return part;
      }

      // It's a word, process it
      const isFirstWord = wordIndex === 0;
      wordIndex++;

      // Handle hyphenated words (e.g., "AI-powered" -> "AI-powered")
      if (part.includes('-')) {
        const hyphenParts = part.split('-');
        const processedParts = hyphenParts.map((subPart, partIndex) => {
          // Check if the part should remain uppercase
          if (upperCaseWords.includes(subPart.toUpperCase())) {
            return subPart.toUpperCase();
          }
          // First part of first word gets capitalized
          if (isFirstWord && partIndex === 0) {
            return subPart.charAt(0).toUpperCase() + subPart.slice(1).toLowerCase();
          }
          // Rest are lowercase
          return subPart.toLowerCase();
        });
        return processedParts.join('-');
      }

      // Check if the word should remain uppercase
      if (upperCaseWords.includes(part.toUpperCase())) {
        return part.toUpperCase();
      }

      // First word in sentence gets first letter capitalized
      if (isFirstWord) {
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }

      // Rest of the words are lowercase
      return part.toLowerCase();
    })
    .join('');
}

/**
 * Transforms markdown content to use sentence case for headings and list items
 * @param content - The markdown content to transform
 * @returns The transformed markdown content
 */
export function transformMarkdownToSentenceCase(content: string): string {
  if (!content) {
    return '';
  }

  return (
    content
      // Transform markdown headings (## Title)
      .replace(/^(\s*)(#{1,6})\s([^\n]+)$/gm, (_match, leadingSpace, hashes, heading) => {
        return `${leadingSpace}${hashes} ${toSentenceCase(heading.trim())}`;
      })
      // Transform list items that look like headings (including nested numbers like "- 5. Title")
      .replace(
        /^(\s*)([-*+])\s+(\d+\.\s+)?(\p{Lu}.+)$/gmu,
        (_match, leadingSpace, bullet, numbering, text) => {
          // Only transform if it starts with an uppercase letter (any language)
          if (text.match(/^\p{Lu}/u)) {
            const transformedText = toSentenceCase(text.trim());
            return `${leadingSpace}${bullet} ${numbering || ''}${transformedText}`;
          }
          return _match;
        },
      )
      // Transform numbered lists that look like headings
      .replace(/^(\s*)(\d+\.)\s+(\p{Lu}.+)$/gmu, (_match, leadingSpace, number, text) => {
        // Only transform if it starts with an uppercase letter (any language)
        if (text.match(/^\p{Lu}/u)) {
          return `${leadingSpace}${number} ${toSentenceCase(text.trim())}`;
        }
        return _match;
      })
  );
}
